"use client";
import Checkbox from "@/components/form/input/Checkbox";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import Button from "@/components/ui/button/Button";
import { EyeCloseIcon, EyeIcon } from "@/icons";
import Link from "next/link";
import React, { useState, useEffect, useRef } from "react";
import { signIn, confirmSignIn, signOut } from "aws-amplify/auth";
import { useRouter, useSearchParams } from "next/navigation";
import AuthFormFooter from "./AuthFormFooter";
import { useTranslations } from "next-intl";
import { goToPageAfterSignIn } from "@/lib/sign-in-redirect";

const MIN_PASSWORD_LENGTH = 8;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A pending MFA challenge from Cognito's classic sign-in flow (company#756). */
type MfaChallenge =
  | { kind: "code"; factor: "TOTP" | "SMS" | "EMAIL" }
  | { kind: "select"; options: ("TOTP" | "SMS" | "EMAIL")[] };

export default function SignInForm() {
  const t = useTranslations("auth");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [missingAttributes, setMissingAttributes] = useState<string[]>([]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  // Set once Cognito's classic sign-in flow returns an MFA challenge step
  // after the password (company#756) — null the rest of the time, so the
  // off-state is byte-identical to sign-in before MFA existed.
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const emailTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const passwordTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();

  // Check if session expired (redirected from api-client)
  useEffect(() => {
    if (searchParams.get("expired") === "true") {
      setSessionExpired(true);
    }
  }, [searchParams]);

  // Clean up debounce timers on unmount
  useEffect(() => {
    return () => {
      if (emailTimerRef.current) clearTimeout(emailTimerRef.current);
      if (passwordTimerRef.current) clearTimeout(passwordTimerRef.current);
    };
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailTimerRef.current) clearTimeout(emailTimerRef.current);
    if (!value || EMAIL_REGEX.test(value)) {
      setEmailError("");
      return;
    }
    emailTimerRef.current = setTimeout(() => {
      setEmailError(t("signin.email_invalid"));
    }, 500);
  };

  const handleEmailBlur = () => {
    if (emailTimerRef.current) clearTimeout(emailTimerRef.current);
    if (email && !EMAIL_REGEX.test(email)) {
      setEmailError(t("signin.email_invalid"));
    }
  };

  const handleSignInPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    if (passwordTimerRef.current) clearTimeout(passwordTimerRef.current);
    if (!value || value.length >= MIN_PASSWORD_LENGTH) {
      setPasswordError("");
      return;
    }
    passwordTimerRef.current = setTimeout(() => {
      setPasswordError(t("signin.password_too_short_hint"));
    }, 500);
  };

  const handleSignInPasswordBlur = () => {
    if (passwordTimerRef.current) clearTimeout(passwordTimerRef.current);
    if (password && password.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(t("signin.password_too_short_hint"));
    }
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await completeSignIn(() => signIn({ username: email, password: password }));
  }

  // Maps a step's next step onto its MfaChallenge shape, or null if it isn't
  // one. Shared by the initial password sign-in and the MFA code/selection
  // confirmations, since a confirmSignIn call could in principle hand back
  // another MFA step too (e.g. a user with both TOTP and SMS enabled but
  // neither preferred, see `useMfa.disable`'s own doc comment).
  function mfaChallengeFor(signInStep: string): MfaChallenge | null {
    switch (signInStep) {
      case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        return { kind: "code", factor: "TOTP" };
      case "CONFIRM_SIGN_IN_WITH_SMS_CODE":
        return { kind: "code", factor: "SMS" };
      case "CONFIRM_SIGN_IN_WITH_EMAIL_CODE":
        return { kind: "code", factor: "EMAIL" };
      default:
        return null;
    }
  }

  async function handleSignInResult(signInResult: { isSignedIn: boolean; nextStep: any }) {
    const { isSignedIn, nextStep } = signInResult;

    console.log('Sign in result:', { isSignedIn, nextStep });

    const mfaCodeChallenge = mfaChallengeFor(nextStep.signInStep);
    if (mfaCodeChallenge) {
      setMfaChallenge(mfaCodeChallenge);
    } else if (nextStep.signInStep === "CONTINUE_SIGN_IN_WITH_MFA_SELECTION") {
      setMfaChallenge({ kind: "select", options: nextStep.allowedMFATypes ?? [] });
    } else if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
      router.push(`/confirm-email?email=${encodeURIComponent(email)}`);
    } else if (nextStep.signInStep === 'RESET_PASSWORD') {
      router.push(`/reset-password?email=${encodeURIComponent(email)}`);
    } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      // User needs to change password (first login with temporary password)
      console.log('Password change required - switching to password change form');
      // Capture missing attributes that Cognito requires
      const missing = (nextStep as any).missingAttributes || [];
      console.log('Missing attributes:', missing);
      setMissingAttributes(missing);
      setNeedsPasswordChange(true);
      console.log('needsPasswordChange state set to true');
    } else if (isSignedIn) {
      goToPageAfterSignIn(searchParams.get("redirect"));
    } else {
      // Log any unexpected step
      console.warn('Unexpected sign in step:', nextStep.signInStep);
      setError('Unexpected authentication state. Please try again.');
    }
  }

  async function completeSignIn(performSignIn: () => ReturnType<typeof signIn>) {
    setError("");
    setLoading(true);

    try {
      let signInResult;
      try {
        signInResult = await performSignIn();
      } catch (err: any) {
        if (err.name === 'UserAlreadyAuthenticatedException') {
          // Stale session in storage — clear it and retry
          await signOut();
          signInResult = await performSignIn();
        } else {
          throw err;
        }
      }
      await handleSignInResult(signInResult);
    } catch (err: any) {
      console.error('Sign in error:', err);
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  }

  // Submits the code for a CONFIRM_SIGN_IN_WITH_{TOTP,SMS,EMAIL}_CODE
  // challenge, or the chosen factor for a CONTINUE_SIGN_IN_WITH_MFA_SELECTION
  // one (company#756) — Cognito's classic MFA flow, a real second step after
  // the password, unlike the earlier passkey approach.
  async function handleMfaChallengeSubmit(challengeResponse: string) {
    setError("");
    setLoading(true);
    try {
      const result = await confirmSignIn({ challengeResponse });
      await handleSignInResult(result);
    } catch (err: any) {
      console.error('MFA confirmation error:', err);
      setError(err.message || 'Failed to verify the code. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function cancelMfaChallenge() {
    setMfaChallenge(null);
    setMfaCode("");
    setError("");
  }

  async function handlePasswordChange(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError(t("signin.passwords_dont_match"));
      return;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      setError(t("signin.password_too_short"));
      return;
    }

    // Validate required attributes
    if (missingAttributes.includes('given_name') && !firstName.trim()) {
      setError(t("signin.first_name_required"));
      return;
    }
    if (missingAttributes.includes('family_name') && !lastName.trim()) {
      setError(t("signin.last_name_required"));
      return;
    }

    setLoading(true);

    try {
      console.log('Confirming new password...');

      // Build user attributes object if there are missing attributes
      const userAttributes: Record<string, string> = {};
      if (missingAttributes.includes('given_name') && firstName.trim()) {
        userAttributes.given_name = firstName.trim();
      }
      if (missingAttributes.includes('family_name') && lastName.trim()) {
        userAttributes.family_name = lastName.trim();
      }

      console.log('User attributes to submit:', userAttributes);

      const result = await confirmSignIn({
        challengeResponse: newPassword,
        options: Object.keys(userAttributes).length > 0 ? { userAttributes } : undefined,
      });

      console.log('Password change result:', result);

      // The password change itself is done regardless of what comes next
      // (signed in, or an MFA challenge for a user who has it enabled) —
      // reset this form's state before handing off to the shared handler.
      setNeedsPasswordChange(false);
      setMissingAttributes([]);
      setNewPassword('');
      setConfirmPassword('');
      setFirstName('');
      setLastName('');

      await handleSignInResult(result);
    } catch (err: any) {
      console.error('Password change error:', err);
      setError(err.message || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col flex-1 lg:w-1/2 w-full overflow-y-auto no-scrollbar">
      <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto px-4">
        <div>
          <div className="mb-5 sm:mb-8">
            <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
              {mfaChallenge
                ? t("signin.mfa_title")
                : needsPasswordChange
                ? t("signin.change_password_title")
                : t("signin.title")}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {mfaChallenge
                ? t("signin.mfa_subtitle")
                : needsPasswordChange
                ? t("signin.change_password_subtitle")
                : t("signin.subtitle")}
            </p>
          </div>
          <div>
            {/*<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-5">
              <button className="inline-flex items-center justify-center gap-3 py-3 text-sm font-normal text-gray-700 transition-colors bg-gray-100 rounded-lg px-7 hover:bg-gray-200 hover:text-gray-800 dark:bg-white/5 dark:text-white/90 dark:hover:bg-white/10">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.7511 10.1944C18.7511 9.47495 18.6915 8.94995 18.5626 8.40552H10.1797V11.6527H15.1003C15.0011 12.4597 14.4654 13.675 13.2749 14.4916L13.2582 14.6003L15.9087 16.6126L16.0924 16.6305C17.7788 15.1041 18.7511 12.8583 18.7511 10.1944Z"
                    fill="#4285F4"
                  />
                  <path
                    d="M10.1788 18.75C12.5895 18.75 14.6133 17.9722 16.0915 16.6305L13.274 14.4916C12.5201 15.0068 11.5081 15.3666 10.1788 15.3666C7.81773 15.3666 5.81379 13.8402 5.09944 11.7305L4.99473 11.7392L2.23868 13.8295L2.20264 13.9277C3.67087 16.786 6.68674 18.75 10.1788 18.75Z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.10014 11.7305C4.91165 11.186 4.80257 10.6027 4.80257 9.99992C4.80257 9.3971 4.91165 8.81379 5.09022 8.26935L5.08523 8.1534L2.29464 6.02954L2.20333 6.0721C1.5982 7.25823 1.25098 8.5902 1.25098 9.99992C1.25098 11.4096 1.5982 12.7415 2.20333 13.9277L5.10014 11.7305Z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M10.1789 4.63331C11.8554 4.63331 12.9864 5.34303 13.6312 5.93612L16.1511 3.525C14.6035 2.11528 12.5895 1.25 10.1789 1.25C6.68676 1.25 3.67088 3.21387 2.20264 6.07218L5.08953 8.26943C5.81381 6.15972 7.81776 4.63331 10.1789 4.63331Z"
                    fill="#EB4335"
                  />
                </svg>
                Sign in with Google
              </button>
              <button className="inline-flex items-center justify-center gap-3 py-3 text-sm font-normal text-gray-700 transition-colors bg-gray-100 rounded-lg px-7 hover:bg-gray-200 hover:text-gray-800 dark:bg-white/5 dark:text-white/90 dark:hover:bg-white/10">
                <svg
                  width="21"
                  className="fill-current"
                  height="20"
                  viewBox="0 0 21 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M15.6705 1.875H18.4272L12.4047 8.75833L19.4897 18.125H13.9422L9.59717 12.4442L4.62554 18.125H1.86721L8.30887 10.7625L1.51221 1.875H7.20054L11.128 7.0675L15.6705 1.875ZM14.703 16.475H16.2305L6.37054 3.43833H4.73137L14.703 16.475Z" />
                </svg>
                Sign in with X
              </button>
            </div>*/}
            {/*<div className="relative py-3 sm:py-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-800"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="p-2 text-gray-400 bg-white dark:bg-gray-900 sm:px-5 sm:py-2">
                  Or
                </span>
              </div>
            </div>*/}
            {mfaChallenge ? (
              // MFA challenge: a code to verify, or a choice among several
              // enabled factors (company#756). A real second step after the
              // password, unlike the earlier passkey approach.
              <div>
                <div className="space-y-6">
                  {error && (
                    <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg dark:bg-red-900/20 dark:text-red-400">
                      {error}
                    </div>
                  )}
                  {mfaChallenge.kind === "select" ? (
                    <div className="space-y-3">
                      {mfaChallenge.options.map((factor) => (
                        <Button
                          key={factor}
                          type="button"
                          variant="outline"
                          size="md"
                          fullWidth
                          disabled={loading}
                          onClick={() => handleMfaChallengeSubmit(factor)}
                        >
                          {t(`signin.mfa_select_${factor.toLowerCase()}`)}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleMfaChallengeSubmit(mfaCode);
                      }}
                    >
                      <div className="space-y-6">
                        <div>
                          <Label>
                            {t(`signin.mfa_code_label_${mfaChallenge.factor.toLowerCase()}`)}{" "}
                            <span className="text-error-500">*</span>
                          </Label>
                          <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            value={mfaCode}
                            onChange={(e) => setMfaCode(e.target.value)}
                            placeholder={t("signin.mfa_code_placeholder")}
                            required
                            className="h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 bg-transparent dark:bg-gray-900 text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/10 dark:border-gray-700 dark:text-white/90 dark:focus:border-brand-800"
                          />
                        </div>
                        <Button type="submit" variant="primary" size="md" fullWidth loading={loading}>
                          {t("signin.mfa_verify_button")}
                        </Button>
                      </div>
                    </form>
                  )}
                  <Button variant="text" size="sm" disabled={loading} onClick={cancelMfaChallenge}>
                    {t("signin.mfa_cancel_button")}
                  </Button>
                </div>
              </div>
            ) : !needsPasswordChange ? (
              // Sign in form
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  {sessionExpired && (
                    <div className="p-3 text-sm text-amber-600 bg-amber-50 rounded-lg dark:bg-amber-900/20 dark:text-amber-400">
                      {t("signin.session_expired")}
                    </div>
                  )}
                  {error && (
                    <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg dark:bg-red-900/20 dark:text-red-400">
                      {error}
                    </div>
                  )}
                  <div>
                    <Label>
                      {t("common.email")} <span className="text-error-500">*</span>{" "}
                    </Label>
                    <input
                      placeholder="info@gmail.com"
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      onBlur={handleEmailBlur}
                      required
                      className={`h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 bg-transparent dark:bg-gray-900 ${
                        emailError
                          ? "border-error-500 text-error-800 focus:border-error-500 focus:ring-error-500/10 dark:border-error-500 dark:text-error-400"
                          : "text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/10 dark:border-gray-700 dark:text-white/90 dark:focus:border-brand-800"
                      }`}
                    />
                    {emailError && (
                      <p className="mt-1 text-xs text-error-500">{emailError}</p>
                    )}
                  </div>
                  <div>
                    <Label>
                      {t("common.password")} <span className="text-error-500">*</span>{" "}
                    </Label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder={t("common.password_placeholder")}
                        value={password}
                        onChange={handleSignInPasswordChange}
                        onBlur={handleSignInPasswordBlur}
                        required
                        className={`h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 bg-transparent dark:bg-gray-900 ${
                          passwordError
                            ? "border-error-500 text-error-800 focus:border-error-500 focus:ring-error-500/10 dark:border-error-500 dark:text-error-400"
                            : "text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/10 dark:border-gray-700 dark:text-white/90 dark:focus:border-brand-800"
                        }`}
                      />
                      <span
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                      >
                        {showPassword ? (
                          <EyeIcon className="fill-gray-500 dark:fill-gray-400" />
                        ) : (
                          <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400" />
                        )}
                      </span>
                    </div>
                    {passwordError && (
                      <p className="mt-1 text-xs text-error-500">{passwordError}</p>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={isChecked} onChange={setIsChecked} />
                      <span className="block font-normal text-gray-700 text-theme-sm dark:text-gray-400">
                        {t("signin.keep_logged_in")}
                      </span>
                    </div>
                    <Link
                      href="/forgot-password"
                      className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                    >
                      {t("signin.forgot_password")}
                    </Link>
                  </div>
                  <div>
                    <Button
                      type="submit"
                      variant="primary"
                      size="md"
                      fullWidth
                      loading={loading}
                    >
                      {t("signin.sign_in_button")}
                    </Button>
                  </div>
                </div>
                {/* Footer links - Privacy & Terms */}
                <AuthFormFooter introText={t("footer.signin_intro")} />
              </form>
            ) : (
              // Change password form
              <form onSubmit={handlePasswordChange}>
                <div className="space-y-6">
                  {error && (
                    <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg dark:bg-red-900/20 dark:text-red-400">
                      {error}
                    </div>
                  )}
                  <div className="p-3 text-sm text-[#a0b9c2] bg-[#a0b9c2]/10 rounded-lg dark:bg-[#a0b9c2]/20 dark:text-[#a0b9c2]">
                    {t("signin.temporary_password_notice")}
                  </div>
                  {/* Show first name field if required by Cognito */}
                  {missingAttributes.includes('given_name') && (
                    <div>
                      <Label>
                        {t("signin.first_name")} <span className="text-error-500">*</span>
                      </Label>
                      <input
                        type="text"
                        placeholder={t("signin.first_name_placeholder")}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        className="h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
                      />
                    </div>
                  )}
                  {/* Show last name field if required by Cognito */}
                  {missingAttributes.includes('family_name') && (
                    <div>
                      <Label>
                        {t("signin.last_name")} <span className="text-error-500">*</span>
                      </Label>
                      <input
                        type="text"
                        placeholder={t("signin.last_name_placeholder")}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        className="h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
                      />
                    </div>
                  )}
                  <div>
                    <Label>
                      {t("signin.new_password")} <span className="text-error-500">*</span>
                    </Label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        placeholder={t("signin.new_password_placeholder")}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        className="h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
                      />
                      <span
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                      >
                        {showNewPassword ? (
                          <EyeIcon className="fill-gray-500 dark:fill-gray-400" />
                        ) : (
                          <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400" />
                        )}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {t("signin.password_requirements")}
                    </p>
                  </div>
                  <div>
                    <Label>
                      {t("signin.confirm_new_password")} <span className="text-error-500">*</span>
                    </Label>
                    <input
                      type="password"
                      placeholder={t("signin.confirm_password_placeholder")}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
                    />
                  </div>
                  <div>
                    <Button
                      type="submit"
                      variant="primary"
                      size="md"
                      fullWidth
                      loading={loading}
                    >
                      {t("signin.change_password_button")}
                    </Button>
                  </div>
                </div>
                {/* Footer links - Privacy & Terms */}
                <AuthFormFooter introText={t("footer.change_password_intro")} />
              </form>
            )}

            {/*<div className="mt-5">
              <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
                Don&apos;t have an account? {""}
                <Link
                  href="/signup"
                  className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                >
                  Sign Up
                </Link>
              </p>
            </div>*/}
          </div>
        </div>
      </div>
    </div>
  );
}
